const LOCATION_BOUNDS = {
  lat: { min: -90, max: 90 },
  lng: { min: -180, max: 180 },
};

const VALID_LOCATION_MODES = ['gps', 'city', 'none'];

const isValidNumber = (value) => typeof value === 'number' && Number.isFinite(value);

const isValidLocationMode = (mode) => VALID_LOCATION_MODES.includes(mode);

const isValidCoordinate = (lat, lng) => {
  if (!isValidNumber(lat) || !isValidNumber(lng)) return false;
  if (lat < LOCATION_BOUNDS.lat.min || lat > LOCATION_BOUNDS.lat.max) return false;
  if (lng < LOCATION_BOUNDS.lng.min || lng > LOCATION_BOUNDS.lng.max) return false;
  return true;
};

const extractCoordinates = (input) => {
  if (!input) return null;

  if (isValidCoordinate(input.latitude, input.longitude)) {
    return { latitude: input.latitude, longitude: input.longitude };
  }

  if (input.coordinates && isValidCoordinate(input.coordinates.latitude, input.coordinates.longitude)) {
    return {
      latitude: input.coordinates.latitude,
      longitude: input.coordinates.longitude,
    };
  }

  if (input.location && isValidCoordinate(input.location.lat, input.location.lng)) {
    return {
      latitude: input.location.lat,
      longitude: input.location.lng,
    };
  }

  if (isValidCoordinate(input.lat, input.lng)) {
    return { latitude: input.lat, longitude: input.lng };
  }

  return null;
};

/**
 * Builds the agent context object for sending to the backend.
 * @param {Object} params - The parameters for building the context.
 * @param {Object} [params.gpsLocation] - GPS location coordinates.
 * @param {Object} [params.profileLocation] - User profile location.
 * @param {Object} [params.preferences] - User preferences.
 * @param {Array} [params.interests] - User interests.
 * @param {Object} [params.overrides] - Override values for location/radius.
 * @param {string} [params.platform='web'] - Platform identifier.
 * @param {string} [params.outputFormat] - Output format requested.
 * @param {string} [params.uiAction] - UI action identifier for button clicks (e.g., 'progress_to_l2', 'progress_to_l3').
 * @returns {Object} The context object to send to the backend.
 */
export const buildAgentContext = ({
  gpsLocation,
  profileLocation,
  preferences,
  interests,
  overrides,
  platform = 'web',
  outputFormat,
  uiAction, // RM-149: Progression externe L1→L2→L3
} = {}) => {
  const gpsCoords = extractCoordinates(gpsLocation);
  const profileCoords = extractCoordinates(profileLocation);
  const overrideCoords = extractCoordinates(overrides?.location);
  const rawOverrideMode = overrides?.location?.mode;
  const overrideMode = isValidLocationMode(rawOverrideMode) ? rawOverrideMode : null;

  const hasOverrideLocation = !!overrideCoords;
  const hasGps = !!gpsCoords;
  const hasProfile = !!profileCoords;

  const location = hasOverrideLocation
    ? { ...overrideCoords, mode: overrideMode || 'city' }
    : overrideMode
      ? { mode: overrideMode }
      : hasGps
        ? { ...gpsCoords, mode: 'gps' }
        : hasProfile
          ? { ...profileCoords, mode: 'city' }
          : { mode: 'none' };

  const radiusFromPrefs = isValidNumber(preferences?.maxDistance) && preferences.maxDistance >= 0
    ? Math.round(preferences.maxDistance * 1000)
    : undefined;
  const radiusMeters = isValidNumber(overrides?.radiusMeters) && overrides.radiusMeters >= 0
    ? overrides.radiusMeters
    : radiusFromPrefs;

  const categories = Array.isArray(overrides?.categories)
    ? overrides.categories
    : Array.isArray(interests)
      ? interests
      : Array.isArray(preferences?.categories)
        ? preferences.categories
        : undefined;

  const timezoneOffset = isValidNumber(overrides?.timezoneOffset)
    ? overrides.timezoneOffset
    : -new Date().getTimezoneOffset();

  // Build preferences only with defined values
  const preferencesPayload = {};
  if (Array.isArray(categories) && categories.length > 0) {
    preferencesPayload.categories = categories;
  }
  if (isValidNumber(radiusMeters)) {
    preferencesPayload.radius = radiusMeters;
  }

  const context = {
    location,
    timezoneOffset,
    platform,
  };

  if (Object.keys(preferencesPayload).length > 0) {
    context.preferences = preferencesPayload;
  }

  if (outputFormat) {
    context.output = { format: outputFormat };
  }

  // RM-149: Ajouter uiAction si fourni (pour progression externe)
  if (uiAction) {
    context.uiAction = uiAction;
  }

  return context;
};

export default buildAgentContext;
